import React, { createContext, useContext, useState } from 'react';
import { api } from '../api';

export interface AdminUser {
  id: string;
  email: string;
  full_name: string;
  is_superadmin: boolean;
}

interface AuthCtx {
  user: AdminUser | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthCtx>(null!);

function readStoredUser(): AdminUser | null {
  try {
    const raw = localStorage.getItem('admin_user');
    const token = localStorage.getItem('admin_token');
    if (!raw || !token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      return null;
    }
    return JSON.parse(raw) as AdminUser;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(readStoredUser);

  const login = async (email: string, password: string) => {
    const data = await api<{ access_token: string; admin_user: AdminUser }>(
      '/admin/auth/login',
      { method: 'POST', body: JSON.stringify({ email, password }) },
    );
    localStorage.setItem('admin_token', data.access_token);
    localStorage.setItem('admin_user', JSON.stringify(data.admin_user));
    setUser(data.admin_user);
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: user !== null, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
